import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Vote,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Copy,
  ExternalLink,
  Search,
  Filter,
  RefreshCw,
  Plus,
  BarChart3,
  Activity,
  Target,
  Award,
  Calendar,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Eye
} from 'lucide-react';

interface Proposal {
  id: string;
  title: string;
  description: string;
  proposer: string;
  proposerName?: string;
  status: 'active' | 'passed' | 'rejected' | 'pending' | 'executed';
  type: 'parameter' | 'upgrade' | 'treasury' | 'text';
  votingStart: string;
  votingEnd: string;
  executionTime?: string;
  votesFor: string;
  votesAgainst: string;
  votesAbstain: string;
  totalVotes: string;
  quorum: number;
  threshold: number;
  participation: number;
  createdAt: string;
  tags?: string[];
}

interface Vote {
  voter: string;
  voterName?: string;
  choice: 'for' | 'against' | 'abstain';
  power: string;
  timestamp: string;
  reason?: string;
}

interface GovernanceStats {
  totalProposals: number;
  activeProposals: number;
  passedProposals: number;
  totalVoters: number;
  totalVotingPower: string;
  averageParticipation: number;
}

const GovernancePage: React.FC = () => {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [stats, setStats] = useState<GovernanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'proposals' | 'votes'>('proposals');
  const proposalsPerPage = 10;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [newProposal, setNewProposal] = useState({
    title: '',
    description: '',
    type: 'text' as 'parameter' | 'upgrade' | 'treasury' | 'text',
    votingDuration: 7
  });

  useEffect(() => {
    fetchGovernanceData();
    const interval = setInterval(fetchGovernanceData, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [filterStatus, filterType, sortBy]);

  const fetchGovernanceData = async () => {
    try {
      setLoading(true);
      
      // Mock 제안 데이터
      const mockProposals: Proposal[] = [
        {
          id: 'prop-1',
          title: 'Increase Block Gas Limit to 30M',
          description: 'Proposal to increase the block gas limit from 15M to 30M to accommodate more transactions per block and improve network throughput.',
          proposer: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
          proposerName: 'Voltron Foundation',
          status: 'active',
          type: 'parameter',
          votingStart: '2024-03-01T00:00:00Z',
          votingEnd: '2024-03-08T00:00:00Z',
          votesFor: '2500000',
          votesAgainst: '800000',
          votesAbstain: '200000',
          totalVotes: '3500000',
          quorum: 10, // 10%
          threshold: 50, // 50%
          participation: 35,
          createdAt: '2024-02-28T00:00:00Z',
          tags: ['network', 'performance']
        },
        {
          id: 'prop-2',
          title: 'Treasury Allocation for AI Development',
          description: 'Allocate 1M VTTN from treasury for AI layer development and research initiatives over the next 6 months.',
          proposer: '0x853e46Dd7645D1542936a4c5E5D3D3D3D3D3D3D3',
          proposerName: 'VOLTRON Energy',
          status: 'passed',
          type: 'treasury',
          votingStart: '2024-02-15T00:00:00Z',
          votingEnd: '2024-02-22T00:00:00Z',
          executionTime: '2024-02-25T00:00:00Z',
          votesFor: '4200000',
          votesAgainst: '1500000',
          votesAbstain: '300000',
          totalVotes: '6000000',
          quorum: 10,
          threshold: 50,
          participation: 60,
          createdAt: '2024-02-12T00:00:00Z',
          tags: ['treasury', 'ai', 'development']
        },
        {
          id: 'prop-3',
          title: 'Validator Commission Cap Reduction',
          description: 'Reduce the maximum validator commission from 15% to 10% to encourage more competitive staking rewards.',
          proposer: '0x964f57Ee8756E2653047b5c6F6E4E4E4E4E4E4E4',
          proposerName: 'BSS Validator',
          status: 'rejected',
          type: 'parameter',
          votingStart: '2024-02-01T00:00:00Z',
          votingEnd: '2024-02-08T00:00:00Z',
          votesFor: '1800000',
          votesAgainst: '3200000',
          votesAbstain: '500000',
          totalVotes: '5500000',
          quorum: 10,
          threshold: 50,
          participation: 55,
          createdAt: '2024-01-29T00:00:00Z',
          tags: ['validators', 'staking']
        }
      ];
      
      // 추가 제안들 생성
      for (let i = 0; i < 12; i++) {
        const statuses = ['active', 'passed', 'rejected', 'pending', 'executed'];
        const types = ['parameter', 'upgrade', 'treasury', 'text'];
        
        mockProposals.push({
          id: `prop-${i + 4}`,
          title: `Proposal ${i + 4}: ${['Network Upgrade', 'Parameter Change', 'Treasury Allocation', 'Protocol Update'][Math.floor(Math.random() * 4)]}`,
          description: `This is a detailed description for proposal ${i + 4}. It outlines the changes and rationale for the proposed modifications to the network.`,
          proposer: `0x${Math.random().toString(16).substring(2, 18)}${Math.random().toString(16).substring(2, 18)}${Math.random().toString(16).substring(2, 10)}`,
          status: statuses[Math.floor(Math.random() * statuses.length)] as 'active' | 'passed' | 'rejected' | 'pending' | 'executed',
          type: types[Math.floor(Math.random() * types.length)] as 'parameter' | 'upgrade' | 'treasury' | 'text',
          votingStart: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
          votingEnd: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          votesFor: (Math.random() * 5000000).toFixed(0),
          votesAgainst: (Math.random() * 3000000).toFixed(0),
          votesAbstain: (Math.random() * 500000).toFixed(0),
          totalVotes: '0',
          quorum: 10,
          threshold: 50,
          participation: Math.random() * 80 + 10,
          createdAt: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(),
          tags: ['governance', 'network']
        });
      }
      
      // totalVotes 계산
      mockProposals.forEach(proposal => {
        proposal.totalVotes = (parseFloat(proposal.votesFor) + parseFloat(proposal.votesAgainst) + parseFloat(proposal.votesAbstain)).toFixed(0);
      });
      
      // Mock 투표 데이터
      const mockVotes: Vote[] = [
        {
          voter: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
          voterName: 'Voltron Foundation',
          choice: 'for',
          power: '1000000',
          timestamp: '2024-03-02T10:30:00Z',
          reason: 'This proposal will significantly improve network performance and user experience.'
        },
        {
          voter: '0x853e46Dd7645D1542936a4c5E5D3D3D3D3D3D3D3',
          voterName: 'VOLTRON Energy',
          choice: 'for',
          power: '800000',
          timestamp: '2024-03-02T14:15:00Z'
        },
        {
          voter: '0x964f57Ee8756E2653047b5c6F6E4E4E4E4E4E4E4',
          voterName: 'BSS Validator',
          choice: 'against',
          power: '600000',
          timestamp: '2024-03-03T09:45:00Z',
          reason: 'Concerns about potential centralization effects.'
        }
      ];
      
      // 필터링
      let filteredProposals = mockProposals;
      
      if (filterStatus !== 'all') {
        filteredProposals = filteredProposals.filter(proposal => proposal.status === filterStatus);
      }
      
      if (filterType !== 'all') {
        filteredProposals = filteredProposals.filter(proposal => proposal.type === filterType);
      }
      
      if (searchTerm) {
        filteredProposals = filteredProposals.filter(proposal => 
          proposal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proposal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (proposal.proposerName && proposal.proposerName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      // 정렬
      filteredProposals.sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          case 'oldest':
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          case 'mostVotes':
            return parseFloat(b.totalVotes) - parseFloat(a.totalVotes);
          case 'participation':
            return b.participation - a.participation;
          default:
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
      });
      
      setProposals(filteredProposals);
      setVotes(mockVotes);
      
      // 통계 계산
      const totalProposals = filteredProposals.length;
      const activeProposals = filteredProposals.filter(p => p.status === 'active').length;
      const passedProposals = filteredProposals.filter(p => p.status === 'passed' || p.status === 'executed').length;
      const totalVotingPower = filteredProposals.reduce((sum, p) => sum + parseFloat(p.totalVotes), 0);
      const averageParticipation = filteredProposals.reduce((sum, p) => sum + p.participation, 0) / filteredProposals.length;
      
      setStats({
        totalProposals,
        activeProposals,
        passedProposals,
        totalVoters: 1234, // Mock data
        totalVotingPower: totalVotingPower.toFixed(0),
        averageParticipation
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch governance data:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toFixed(0);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Vote className="w-4 h-4 text-blue-500" />;
      case 'passed':
      case 'executed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'passed': return 'bg-green-100 text-green-800';
      case 'executed': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'parameter': return 'bg-orange-100 text-orange-800';
      case 'upgrade': return 'bg-purple-100 text-purple-800';
      case 'treasury': return 'bg-green-100 text-green-800';
      case 'text': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVoteChoice = (choice: string) => {
    switch (choice) {
      case 'for':
        return { icon: <ThumbsUp className="w-4 h-4" />, color: 'text-green-600', bg: 'bg-green-100' };
      case 'against':
        return { icon: <ThumbsDown className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-100' };
      case 'abstain':
        return { icon: <Eye className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-100' };
      default:
        return { icon: <Eye className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatTimeRemaining = (endTime: string) => {
    const seconds = Math.floor((new Date(endTime).getTime() - Date.now()) / 1000);
    if (seconds <= 0) return 'Ended';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m left`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h left`;
    return `${Math.floor(seconds / 86400)}d left`;
  };

  const calculateVotePercentage = (votes: string, total: string) => {
    const totalNum = parseFloat(total);
    if (totalNum === 0) return 0;
    return (parseFloat(votes) / totalNum) * 100;
  };

  const paginatedProposals = proposals.slice(
    (currentPage - 1) * proposalsPerPage,
    currentPage * proposalsPerPage
  );

  const totalPages = Math.ceil(proposals.length / proposalsPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Governance</h1>
          <p className="text-slate-600">Participate in network governance and decision making</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchGovernanceData}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Proposal</span>
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Proposals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalProposals}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Proposals</p>
                <p className="text-2xl font-bold text-blue-600">{stats.activeProposals}</p>
              </div>
              <Vote className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Passed Proposals</p>
                <p className="text-2xl font-bold text-green-600">{stats.passedProposals}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Voters</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalVoters.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Voting Power</p>
                <p className="text-2xl font-bold text-indigo-600">{formatNumber(stats.totalVotingPower)} VTTN</p>
              </div>
              <BarChart3 className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Participation</p>
                <p className="text-2xl font-bold text-teal-600">{stats.averageParticipation.toFixed(1)}%</p>
              </div>
              <Target className="w-8 h-8 text-teal-500" />
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search proposals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="passed">Passed</option>
            <option value="rejected">Rejected</option>
            <option value="pending">Pending</option>
            <option value="executed">Executed</option>
          </select>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="parameter">Parameter</option>
            <option value="upgrade">Upgrade</option>
            <option value="treasury">Treasury</option>
            <option value="text">Text</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="mostVotes">Most Votes</option>
            <option value="participation">Participation</option>
          </select>
        </div>
      </div>

      {/* 제안 목록 */}
      <div className="space-y-4">
        {loading ? (
          Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
                <div className="h-6 w-20 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          ))
        ) : paginatedProposals.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">No proposals found</p>
            <p className="text-sm text-gray-400">Be the first to create a proposal!</p>
          </div>
        ) : (
          paginatedProposals.map((proposal) => {
            const forPercentage = calculateVotePercentage(proposal.votesFor, proposal.totalVotes);
            const againstPercentage = calculateVotePercentage(proposal.votesAgainst, proposal.totalVotes);
            const abstainPercentage = calculateVotePercentage(proposal.votesAbstain, proposal.totalVotes);
            
            return (
              <div key={proposal.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Link 
                        to={`/governance/${proposal.id}`}
                        className="text-xl font-semibold text-gray-900 hover:text-orange-600 transition-colors"
                      >
                        {proposal.title}
                      </Link>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(proposal.type)}`}>
                        {proposal.type}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3 line-clamp-2">{proposal.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <span>By</span>
                        <span className="font-medium">
                          {proposal.proposerName || formatAddress(proposal.proposer)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(proposal.proposer)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <span>•</span>
                      <span>{formatTimeAgo(proposal.createdAt)}</span>
                      {proposal.status === 'active' && (
                        <>
                          <span>•</span>
                          <span className="text-blue-600 font-medium">
                            {formatTimeRemaining(proposal.votingEnd)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(proposal.status)}
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(proposal.status)}`}>
                      {proposal.status}
                    </span>
                  </div>
                </div>
                
                {/* 투표 진행률 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Voting Progress</span>
                    <span className="text-gray-900 font-medium">
                      {formatNumber(proposal.totalVotes)} VTTN ({proposal.participation.toFixed(1)}% participation)
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {/* For votes */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 w-20">
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-600">For</span>
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${forPercentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">
                        {forPercentage.toFixed(1)}%
                      </span>
                    </div>
                    
                    {/* Against votes */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 w-20">
                        <ThumbsDown className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-600">Against</span>
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${againstPercentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">
                        {againstPercentage.toFixed(1)}%
                      </span>
                    </div>
                    
                    {/* Abstain votes */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 w-20">
                        <Eye className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-600">Abstain</span>
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gray-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${abstainPercentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">
                        {abstainPercentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  
                  {/* 쿼럼 및 임계값 정보 */}
                  <div className="flex justify-between items-center text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span>Quorum: {proposal.quorum}% | Threshold: {proposal.threshold}%</span>
                    <span>
                      {proposal.participation >= proposal.quorum ? (
                        <span className="text-green-600">✓ Quorum met</span>
                      ) : (
                        <span className="text-red-600">✗ Quorum not met</span>
                      )}
                    </span>
                  </div>
                </div>
                
                {/* 액션 버튼 */}
                {proposal.status === 'active' && (
                  <div className="flex space-x-3 mt-4 pt-4 border-t border-gray-100">
                    <button className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                      Vote For
                    </button>
                    <button className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                      Vote Against
                    </button>
                    <button className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                      Abstain
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 border rounded-lg ${
                  currentPage === page
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* 제안 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Create New Proposal</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proposal Title
                </label>
                <input
                  type="text"
                  value={newProposal.title}
                  onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter proposal title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proposal Type
                </label>
                <select
                  value={newProposal.type}
                  onChange={(e) => setNewProposal({ ...newProposal, type: e.target.value as 'parameter' | 'upgrade' | 'treasury' | 'text' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="text">Text Proposal</option>
                  <option value="parameter">Parameter Change</option>
                  <option value="upgrade">Protocol Upgrade</option>
                  <option value="treasury">Treasury Allocation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newProposal.description}
                  onChange={(e) => setNewProposal({ ...newProposal, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Provide a detailed description of your proposal..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voting Duration (days)
                </label>
                <select
                  value={newProposal.votingDuration}
                  onChange={(e) => setNewProposal({ ...newProposal, votingDuration: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Requirements</h4>
                    <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                      <li>• Minimum 1,000 VTTN required to create a proposal</li>
                      <li>• Proposal will enter a 24-hour review period before voting begins</li>
                      <li>• Ensure your proposal follows community guidelines</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Create Proposal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default GovernancePage;
